import { Box, Grid, Group, Text } from "@mantine/core";
import ReactApexChart from "react-apexcharts";
import LeadsBySource from "../../../sales/components/Charts/LeadsBySource";
import { DASHBOARD_EMPTY_STATE } from "../../constants";
import adminClasses from "../AdminDashboardView/AdminDashboardView.module.css";
import salesClasses from "./SalesDashboardView.module.css";
import { CHART_MIN_HEIGHT, FONT, PIPELINE_COLORS, SOURCE_COLORS } from "./salesDashboard.constants";

const chartBase = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, fontFamily: FONT },
  grid: { borderColor: "#eef0f4", strokeDashArray: 4, padding: { left: 2, right: 8 } },
  dataLabels: { enabled: false },
  tooltip: { theme: "light", style: { fontSize: "12px", fontFamily: FONT }, intersect: false },
};

const sourceColor = (key) => SOURCE_COLORS[key] || SOURCE_COLORS[String(key).toLowerCase()] || "#6366f1";

const Panel = ({ title, subtitle, loading, hasData, children }) => (
  <div className={adminClasses.card}>
    {subtitle ? (
      <Group justify="space-between" align="flex-start" mb={8} wrap="nowrap">
        <div className={adminClasses.cardHeader}>{title}</div>
        <Text size="xs" c="dimmed" ta="right" maw="55%">
          {subtitle}
        </Text>
      </Group>
    ) : (
      <div className={adminClasses.cardHeader}>{title}</div>
    )}
    {loading ? (
      <div className={adminClasses.emptyState} style={{ minHeight: CHART_MIN_HEIGHT }}>
        <Text size="sm" c="dimmed">
          Loading...
        </Text>
      </div>
    ) : hasData ? (
      children
    ) : (
      <div className={adminClasses.emptyState} style={{ minHeight: CHART_MIN_HEIGHT }}>
        {DASHBOARD_EMPTY_STATE}
      </div>
    )}
  </div>
);

const SalesDashboardCharts = ({ dashboard }) => {
  const { pipelineData, leadSourcesData, weeklyLeadsData, isStatsLoading } = dashboard;
  const rows = pipelineData?.pipeline ?? [];
  const max = pipelineData?.max_count ?? 1;
  const days = weeklyLeadsData?.days ?? [];
  const sourceChart = (leadSourcesData?.sources ?? [])
    .filter((item) => item.count > 0)
    .map((item) => ({
      label: item.label,
      value: item.count,
      color: sourceColor(item.key),
    }));

  return (
    <Grid gutter={12}>
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Panel title="Lead Pipeline" loading={isStatsLoading} hasData={rows.length > 0}>
          <div className={salesClasses.pipelineStack}>
            {rows.map((row, index) => (
              <div key={row.key ?? index} className={salesClasses.pipelineRow}>
                <Text className={salesClasses.pipelineLabel}>{row.stage}</Text>
                <div className={salesClasses.pipelineTrack}>
                  <Box
                    className={salesClasses.pipelineFill}
                    style={{
                      width: `${(row.count / max) * 100}%`,
                      minWidth: row.count > 0 ? 8 : 0,
                      background: PIPELINE_COLORS[row.key] || "#6366f1",
                    }}
                  />
                </div>
                <Text className={salesClasses.pipelineValue}>{row.count}</Text>
              </div>
            ))}
          </div>
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Panel
          title="New Leads — 7 Days"
          subtitle={weeklyLeadsData?.summaryText}
          loading={isStatsLoading}
          hasData={days.length > 0}
        >
          <ReactApexChart
            options={{
              ...chartBase,
              chart: { ...chartBase.chart, type: "area" },
              colors: ["#7c3aed"],
              stroke: { width: 2.5, curve: "smooth" },
              fill: {
                type: "gradient",
                gradient: { shade: "light", type: "vertical", opacityFrom: 0.35, opacityTo: 0.04 },
              },
              xaxis: {
                categories: days.map((day) => {
                  const date = new Date(day.date);
                  return Number.isNaN(date.getTime())
                    ? day.date
                    : date.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 2);
                }),
                labels: { style: { fontSize: "10px" } },
              },
              yaxis: { labels: { formatter: (value) => Math.round(value) } },
              tooltip: { ...chartBase.tooltip, y: { formatter: (value) => `${Math.round(value)} leads` } },
            }}
            series={[{ name: "New leads", data: days.map((day) => day.count) }]}
            type="area"
            height={CHART_MIN_HEIGHT}
          />
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Panel title="Lead Sources" loading={isStatsLoading} hasData={sourceChart.length > 0}>
          <LeadsBySource sourceChart={sourceChart} />
        </Panel>
      </Grid.Col>
    </Grid>
  );
};

export default SalesDashboardCharts;
